import Keycloak, { KeycloakInstance } from 'keycloak-js';

import { loggerService } from 'shared/lib/logger';

import {
  getKeycloakConfig,
  keycloakInitConfig,
  UPDATE_TOKEN_INTERVAL,
} from './config';
import { KeycloakProfileWithAttributes, UserRoles } from './types';

class AuthService {
  private _kc: KeycloakInstance | null = null;
  private tokenRefreshInterval!: number;

  async init() {
    try {
      this._kc = Keycloak(getKeycloakConfig());
      await this._kc.init(keycloakInitConfig);
      // load user profile so it is available in _kc
      await this._kc.loadUserProfile();
      // for convenience
      window.logout = this._kc.logout;
    } catch (e) {
      loggerService.logError('AuthService initialization error.', e);
      this.logout();
    }

    this.tokenRefreshInterval = window.setInterval(async () => {
      try {
        const refreshed = await this.updateToken();
        if (refreshed) {
          const event = new CustomEvent('tokenRefreshed');
          window.dispatchEvent(event);
        }
      } catch (e) {
        console.error(e);
        this.logout();
      }
    }, UPDATE_TOKEN_INTERVAL);
  }

  getKC() {
    return this._kc;
  }

  updateToken() {
    // if token expires within 30 seconds - refresh it
    return this._kc?.updateToken(30) ?? Promise.reject();
  }

  // needed for amp-navigation
  getParsedToken() {
    return this._kc?.tokenParsed ?? {};
  }

  getAccessToken() {
    return this._kc?.token ?? '';
  }

  getUserBk() {
    return this._kc?.subject ?? '';
  }

  getSessionId() {
    return this._kc?.sessionId ?? '';
  }

  getMclassUserSid() {
    return this._kc?.tokenParsed?.mclassUserSid ?? '';
  }

  getUserProfile() {
    return (this._kc?.profile ?? {}) as KeycloakProfileWithAttributes;
  }

  logout() {
    try {
      clearInterval(this.tokenRefreshInterval);
      this._kc?.logout();
    } catch (e) {
      console.error('Failed to logout');
    }
  }

  hasRole(searchRole: UserRoles) {
    return !!this._kc?.tokenParsed?.realm_access?.roles.some(
      (role) => role.indexOf(searchRole) >= 0,
    );
  }

  isReadOnly() {
    return this.hasRole(UserRoles.READ_ONLY);
  }

  getRole(): UserRoles {
    return this.hasRole(UserRoles.TEACHER)
      ? UserRoles.TEACHER
      : UserRoles.STUDENT;
  }

  isReviewer() {
    return (
      this.hasRole(UserRoles.ROLE_REVIEWER) ||
      this.hasRole(UserRoles.REVIEWER) ||
      this.hasRole(UserRoles.READ_ONLY)
    );
  }
}

const authService = new AuthService();
export default authService;
